import React, { useEffect, useState, useMemo } from "react";
import Theme from "@amzn/meridian/theme";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Tag from "@amzn/meridian/tag";
import Select, { SelectOption } from "@amzn/meridian/select";
import { colorBlue500 } from "@amzn/meridian-tokens/base/color";
import MetricsSection from "../../../../components/MetricsSection/v2/MetricsSection";
import ShiftDataTable from "../../../../components/ShiftDataTable/v2/ShiftDataTableContainer";
import ShiftTotalsSection from "../../../../components/ShiftTotalsSection/v2/ShiftTotalsSectionContainer";
import Chart from "../../../reviewAndPublish/components/v2/Chart";
import PlanActionsCard from "./PlanActionsCard";
import { SPMSGetShiftPlanResponse, PlanVersion } from "../../../../types/shiftPlan";
import { getYesterdayDate } from "../../models";
import { PLAN_VIEW_OPTIONS, DEFAULT_PLAN_VIEW } from "./planViewOptions";
import { findBaselinePlan, getOverridePlans } from "./baselinePlan";
import { getShiftPlanOutput as fetchShiftPlanOutput } from "../../../reviewAndPublish/api";
import { buildGetShiftPlanOutputRequest, GetShiftPlanOutputRequest, ProjectedVolumeDataPoint, GetShiftPlanOutputResponse } from "../../../reviewAndPublish/model";
import { createShiftPlan, getClockWindowNameBasedOnSelectedOption, getPlanTypeBasedOnSelectedTab } from "../../../../services/shiftPlanService";
import { useAppDispatch } from "../../../../store";
import { showNotification } from "../../../../store/slices/errorSlice";
import { ErrorCode, PlanStatus } from "../../../../utils/constants";
import { convertUTCToLocalTime } from "../../../../utils/string-utils";

/**
 * Refactored (V2) published shift plan page — UTR-REFLEX-1163.
 *
 * Built incrementally behind the RCC_PUBLISHED_SHIFT_PLAN_V2 gate. So far this
 * page renders the header (Plan View + Version selectors, "Viewing" badge,
 * Start-New-Shift-Plan button — UTR-REFLEX-1166/1167), the three metric cards
 * (UTR-REFLEX-1168), and the Process Path table (UTR-REFLEX-1165). The chart and
 * shift totals land in follow-up CRs. Kept as a sibling of the V1 page
 * (CurrentPlanSummaryContainer) so V1 is never touched. Layout and surfaces come
 * from Meridian (Theme/Box/Row/Column/Tag/Select), so there's no custom CSS.
 */
interface CurrentPlanSummaryProps {
    shiftPlans: SPMSGetShiftPlanResponse[];
    selectedTab: string;
    selectedDate: string;
    selectedShift: string;
    /** Called when plan creation reports an existing plan — parent opens the modal. */
    onNeedsTakeOver: (displayMessage: string) => void;
    /** Called on successful plan creation — parent handles dispatch + navigate. */
    onPlanSuccess: (planId: string) => void;
}

interface CurrentPlanSummaryStateProps {
    planId: string | null;
    stationCode: string | null;
    shiftPlanOutput?: {
        planId?: string;
        planStatus?: string;
        overrides?: { total?: number };
        totalOverrides?: number;
        shiftSummary?: unknown;
        projectedPrimaryGradingVolume?: ProjectedVolumeDataPoint[];
    };
}

interface CurrentPlanSummaryDispatchProps {
    getShiftPlanOutput: (getShiftPlanOutputRequest: GetShiftPlanOutputRequest) => void;
    showError: (errorPayload: { title: string; message: string }) => void;
}

type Props = CurrentPlanSummaryProps & CurrentPlanSummaryStateProps & CurrentPlanSummaryDispatchProps;

const CurrentPlanSummary: React.FC<Props> = ({
    shiftPlans,
    selectedTab,
    selectedDate,
    selectedShift,
    stationCode,
    shiftPlanOutput,
    getShiftPlanOutput,
    showError,
    onNeedsTakeOver,
    onPlanSuccess,
}) => {
    const dispatch = useAppDispatch();
    const [selectedPlan, setSelectedPlan] = useState(shiftPlans[0]);
    const [selectedPlanView, setSelectedPlanView] = useState<string>(DEFAULT_PLAN_VIEW);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    // The "other" plan's projected curve, overlaid on the chart when both plans
    // exist. Fetched via getShiftPlanOutput (not the shared thunk — that single
    // slot feeds the primary/selected plan) and kept in local state.
    const [overlaySeries, setOverlaySeries] = useState<ProjectedVolumeDataPoint[] | null>(null);

    // Short label of the currently selected Plan View, shown in the "Viewing" badge
    // (without the dropdown's "(Latest Published)" suffix). selectedPlanView is always
    // one of PLAN_VIEW_OPTIONS (init from DEFAULT_PLAN_VIEW, only changed via the
    // dropdown), so the lookup always matches.
    const selectedPlanViewLabel =
        PLAN_VIEW_OPTIONS.find((option) => option.value === selectedPlanView)!.badgeLabel;

    const isRccProposedView = selectedPlanView === 'RCC_PROPOSED_LATEST_PUBLISHED';

    // The auto-generated baseline plan (RCC Proposed), if one exists. Identified via
    // the dedicated isBaselinePlan helper (single swap point for the future
    // auto-generation implementation).
    const baselinePlan = useMemo(() => findBaselinePlan(shiftPlans), [shiftPlans]);

    // Versions shown in the Version dropdown depend on the Plan View:
    //  - RCC Proposed Plan: only the single auto-generated baseline plan (if any).
    //  - Operator Override Plan: all non-baseline (operator-created) plans.
    // NOTE: derived client-side from the mock; the backend returns the correct
    // per-view version set in UTR-REFLEX-1172.
    const visibleVersions = useMemo(() => {
        if (isRccProposedView) {
            return baselinePlan ? [baselinePlan] : [];
        }
        return getOverridePlans(shiftPlans);
    }, [isRccProposedView, shiftPlans, baselinePlan]);

    // The most recently updated plan among the visible versions — the "(Latest)"
    // marker in the Version dropdown (per view, since each view lists a different set).
    const latestPlanId = useMemo(() => {
        if (visibleVersions.length === 0) {
            return undefined;
        }
        return visibleVersions.reduce((latest, plan) => {
            const latestTime = new Date(latest.updatedAt as string).getTime();
            const planTime = new Date(plan.updatedAt as string).getTime();
            return planTime > latestTime ? plan : latest;
        }).planId;
    }, [visibleVersions]);

    // Clean empty state whenever the selected view has no plans (RCC Proposed with
    // no baseline yet, or Operator Override with no operator plans).
    const showEmptyState = visibleVersions.length === 0;
    const emptyStateTitle = isRccProposedView
        ? 'No RCC Proposed Plan available'
        : 'No Operator Override Plan available';
    const emptyStateMessage = isRccProposedView
        ? "An auto-generated RCC Proposed Plan hasn't been published for this shift yet. Once it's generated, its metrics and process path breakdown will appear here."
        : "No Operator Override Plan has been created for this shift yet. Once an operator overrides the RCC Proposed Plan, it will appear here.";

    // Keep the selected version valid when the Plan View (and thus the version list)
    // changes — fall back to the first visible version.
    useEffect(() => {
        if (visibleVersions.length === 0) {
            return;
        }
        const stillVisible = visibleVersions.some((plan) => plan.planId === selectedPlan?.planId);
        if (!stillVisible) {
            setSelectedPlan(visibleVersions[0]);
        }
    }, [visibleVersions, selectedPlan]);

    // Refetch when the selected version OR the plan view changes — each view returns
    // its own metrics, totals, and series. Only fetch for a plan that's actually in
    // the current view's version list, so switching to an empty view doesn't fetch
    // with a stale plan id from the previous view.
    const selectedPlanInView = visibleVersions.some((plan) => plan.planId === selectedPlan?.planId);
    useEffect(() => {
        if (selectedPlanInView && selectedPlan?.planId && selectedDate && stationCode && selectedShift) {
            getShiftPlanOutput(
                buildGetShiftPlanOutputRequest(
                    selectedPlan.planId as string, selectedDate, stationCode, selectedShift, selectedPlanView
                )
            );
        }
    }, [selectedPlanInView, selectedPlan?.planId, selectedPlanView, selectedDate, stationCode, selectedShift, getShiftPlanOutput]);

    // The "other" plan whose curve overlays the chart when both plans exist:
    //  - RCC view    → overlay the (latest) operator override plan, if any.
    //  - Override view → overlay the RCC baseline plan, if any.
    // Overlay plan (the second line on the chart), per product:
    //  - RCC Proposed view: NO overlay — the RCC plan shows only its black line.
    //  - Operator Override view: overlay the RCC baseline, so the override plan
    //    shows both its orange line and the black RCC line for comparison.
    const overlayPlan = useMemo(
        () => (isRccProposedView ? undefined : baselinePlan),
        [isRccProposedView, baselinePlan]
    );

    // Fetch the overlay plan's curve directly (bypassing the shared thunk, which
    // feeds only the primary/selected plan) and hold it in local state. Cleared
    // when no overlay plan exists so a stale curve doesn't linger across views.
    useEffect(() => {
        let cancelled = false;
        if (overlayPlan?.planId && selectedDate && stationCode && selectedShift) {
            fetchShiftPlanOutput(
                buildGetShiftPlanOutputRequest(
                    overlayPlan.planId as string, selectedDate, stationCode, selectedShift, selectedPlanView
                )
            )
                .then((output) => { if (!cancelled) setOverlaySeries(output?.projectedPrimaryGradingVolume ?? null); })
                .catch(() => { if (!cancelled) setOverlaySeries(null); });
        } else {
            setOverlaySeries(null);
        }
        return () => { cancelled = true; };
    }, [overlayPlan?.planId, selectedPlanView, selectedDate, stationCode, selectedShift]);

    useEffect(() => {
        if (shiftPlanOutput && shiftPlanOutput.planStatus) {
            const status = shiftPlanOutput.planStatus;
            if (status !== PlanStatus.PENDING_APPROVAL && status !== PlanStatus.APPROVED) {
                showError({
                    title: "Invalid Plan Status",
                    message: status === PlanStatus.CLOSED
                        ? "This plan is closed. Please select any other valid plan"
                        : "This plan is not valid. Please select any other valid plan",
                });
            }
        }
    }, [shiftPlanOutput, showError]);

    const handleSelectChange = (value: string) => {
        setSelectedPlan(shiftPlans.find((plan) => plan.planId === value) as SPMSGetShiftPlanResponse);
    };

    const onGeneratePlanClick = async () => {
        if (new Date(selectedDate) < new Date(getYesterdayDate())) {
            dispatch(showNotification({
                title: "Invalid Date",
                message: "Cannot generate plan for past date. Please try generating for another date.",
                type: "warning",
            }));
            return;
        }
        setIsGenerating(true);
        try {
            const response = await createShiftPlan(selectedDate,
                getClockWindowNameBasedOnSelectedOption(selectedShift), getPlanTypeBasedOnSelectedTab(selectedTab), PlanVersion.STATIC_V1);
            if (response.errorDetails?.errorCode === ErrorCode.PLAN_OWNED_BY_ANOTHER_USER) {
                onNeedsTakeOver(response.errorDetails.errorMessage);
            } else if (response.status && response.status !== 'FAILED' && response.planId) {
                onPlanSuccess(response.planId);
            }
        } catch (error) {
            dispatch(showNotification({
                title: "Error",
                message: "Failed to create shift plan. Please try again.",
                type: "error",
            }));
        } finally {
            // Always clear the loading state so the button can't stick on "Generating...".
            setIsGenerating(false);
        }
    };

    // Include isGenerating so the button can't be re-clicked mid-request (which
    // would fire concurrent createShiftPlan calls).
    const disableGenerateButton = !selectedDate || !selectedPlan || !selectedShift || isGenerating;

    // Chart series: the primary (selected) plan's curve comes from the shared
    // shiftPlanOutput; the overlay plan's curve is the locally-fetched overlaySeries.
    // Which is RCC vs Override depends on the view — in the RCC view the primary IS
    // the RCC line and the overlay is the override; in the Override view it's flipped.
    const primarySeries = shiftPlanOutput?.projectedPrimaryGradingVolume ?? [];
    const rccSeries = isRccProposedView ? primarySeries : (overlaySeries ?? []);
    const overrideSeries = isRccProposedView ? (overlaySeries ?? []) : primarySeries;

    // Local time of when the selected plan was last generated (empty if unknown) —
    // reused for the Plan Actions "last generated at" line and the PDF metadata.
    const selectedPlanUpdatedAt = selectedPlan?.updatedAt
        ? convertUTCToLocalTime(selectedPlan.updatedAt as string)
        : '';

    return (
        // Nested theme: the app-wide theme is purple, but the V2 mocks call for blue
        // primary surfaces. Uses Meridian's built-in "blue" palette, scoped here so
        // V1 and the rest of the app keep their theme. `mode` (light/dark) inherited.
        <Theme palette="blue">
            <Box spacingInset="300">
                <Column spacing="400">
                    <Row alignmentVertical="top" spacing="400" widths={["fit", "fit", "fit", "fill", "fit"]}>
                        <Select label="Plan View" value={selectedPlanView} onChange={setSelectedPlanView} width={355}>
                            {PLAN_VIEW_OPTIONS.map((option) => (
                                <SelectOption key={option.value} label={option.label} value={option.value} />
                            ))}
                        </Select>
                        <Select label="Version" value={selectedPlan?.planId as string} onChange={handleSelectChange} width={300}>
                            {visibleVersions.map((plan) => (
                                <SelectOption
                                    key={plan.planId as string}
                                    label={
                                        plan.planId === latestPlanId
                                            ? `${convertUTCToLocalTime(plan.updatedAt as string)} (Latest)`
                                            : convertUTCToLocalTime(plan.updatedAt as string)
                                    }
                                    value={plan.planId as string}
                                />
                            ))}
                        </Select>
                        <Box width={3} minHeight={85} backgroundColor={colorBlue500} aria-hidden="true" />
                        <Column spacing="100" alignmentVertical="top">
                            <Text type="h100">
                                Current plan input summary ({shiftPlanOutput?.totalOverrides ?? 0} overrides)
                            </Text>
                            <Text type="b100" color="secondary">
                                Plan id: {shiftPlanOutput?.planId ?? ''}
                            </Text>
                            {/* Row wrapper so the Tag hugs its text instead of
                                stretching to the Column's full width. */}
                            <Row>
                                <Tag type="warning">{`Viewing: ${selectedPlanViewLabel}`}</Tag>
                            </Row>
                        </Column>

                        <Button type="primary" onClick={onGeneratePlanClick} disabled={disableGenerateButton}>
                            {isGenerating ? "Generating..." : "Start New Shift Plan"}
                        </Button>
                    </Row>

                    {showEmptyState ? (
                        <Box type="outline" spacingInset="600">
                            <Column spacing="200" alignmentHorizontal="center">
                                <Heading type="h400" level={2}>{emptyStateTitle}</Heading>
                                <Text type="b300" color="secondary" alignment="center">{emptyStateMessage}</Text>
                            </Column>
                        </Box>
                    ) : (
                        <>
                            <MetricsSection />

                            <Row widths={["fill", 440]} spacing="400" alignmentVertical="stretch">
                                <Box type="outline" spacingInset="400">
                                    <Column spacing="300">
                                        <Heading type="h500" level={2}>RCC Recommended Volume</Heading>
                                        <Chart rccSeries={rccSeries} overrideSeries={overrideSeries} />
                                    </Column>
                                </Box>
                                <Box type="outline" spacingInset="400">
                                    <ShiftTotalsSection />
                                </Box>
                            </Row>

                            <ShiftDataTable />

                            <PlanActionsCard
                                shiftPlanOutput={shiftPlanOutput as GetShiftPlanOutputResponse | undefined}
                                pdfMetadata={{
                                    planType: selectedPlanViewLabel,
                                    isRccProposedView,
                                    version: selectedPlanUpdatedAt,
                                    date: selectedDate,
                                    shift: selectedShift,
                                    stationCode: stationCode ?? '',
                                }}
                                lastGeneratedBy={selectedPlan?.lastUpdatedBy as string | undefined}
                                lastGeneratedAt={selectedPlanUpdatedAt || undefined}
                            />
                        </>
                    )}
                </Column>
            </Box>
        </Theme>
    );
};

export default CurrentPlanSummary;
